// src/components/Billing/ScheduledChangeBanner.jsx

import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Stack,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  alpha,
  Link,
  CircularProgress,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useBillings } from "../../contexts/BillingContext";
import { priceIdToPlanKey } from "../../pricingtable/planConfigs";

export default function ScheduledChangeBanner() {
  /* ───────────────────── hooks (ALWAYS called) ───────────────────── */
  const location = useLocation();
  const billingsCtx = useBillings() || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentSubscription = billingsCtx.currentSubscription ?? null;
  const cancelScheduledDowngrade =
    billingsCtx.cancelScheduledDowngrade ?? null;
  const syncStripeSubscriptionDetails =
    billingsCtx.syncStripeSubscriptionDetails ?? null;

  const [isCancelingScheduledDowngrade, setIsCancelingScheduledDowngrade] =
    useState(false);

  /* ───────────────────────── guards ──────────────────────────────── */
  const isOnSubscriptionPage = location.pathname === "/subscription";
  const scheduled = currentSubscription?.pendingStripeSchedule || null;
  const hasScheduledChange = Boolean(scheduled?.effectiveAt);

  /* ───────────────────── display helpers ─────────────────────────── */
  const effectiveAt = scheduled?.effectiveAt || null;
  const scheduledDate = effectiveAt
    ? new Date(effectiveAt).toLocaleDateString(
        undefined,
        {month: "short", day: "numeric", year: "numeric"},
      )
    : "—";

  const scheduledSeats = Number.isFinite(+scheduled?.newSeatCount)
    ? Number(scheduled.newSeatCount)
    : null;

  const currentSeats = Number.isFinite(+currentSubscription?.seats)
    ? Number(currentSubscription.seats)
    : null;

  const seatsWillChange =
    scheduledSeats != null &&
    currentSeats != null &&
    scheduledSeats !== currentSeats;

  const scheduledPlanKey =
    scheduled?.newPriceId && priceIdToPlanKey[scheduled.newPriceId]
      ? priceIdToPlanKey[scheduled.newPriceId]
      : null;

  const scheduledPlanName = scheduledPlanKey
    ? scheduledPlanKey.charAt(0).toUpperCase() + scheduledPlanKey.slice(1)
    : null;

  const currentPlanName =
    currentSubscription?.planName || null;

  const normalizedCurrentPlanName =
    (currentPlanName || "").toString().trim().toLowerCase();

  const normalizedScheduledPlanName =
    (scheduledPlanName || "").toString().trim().toLowerCase();

  const planWillChange =
    Boolean(normalizedScheduledPlanName) &&
    normalizedScheduledPlanName !== normalizedCurrentPlanName;

  const bannerText = useMemo(() => {
    const parts = [];

    parts.push("Your changes will take effect on ");

    parts.push({
      strong: scheduledDate,
    });

    parts.push(". ");

    if (planWillChange) {
      parts.push("Your plan will change from ");
      parts.push({
        strong: currentPlanName,
      });
      parts.push(" to ");
      parts.push({
        strong: scheduledPlanName,
      });
      parts.push(". ");
    }

    if (seatsWillChange) {
      parts.push("Seats will change from ");
      parts.push({
        strong: currentSeats,
      });
      parts.push(" to ");
      parts.push({
        strong: scheduledSeats,
      });
      parts.push(". ");
    }

    parts.push("You’ll keep your current plan and seats until then.");

    return parts;
  }, [
    scheduledDate,
    planWillChange,
    currentPlanName,
    scheduledPlanName,
    seatsWillChange,
    currentSeats,
    scheduledSeats,
  ]);

  /* ────────────────────────────────────────────────────────────────
   * ENTERPRISE-GRADE BEHAVIOR:
   * - This banner is NOT dismissible.
   * - Visibility is derived strictly from backend billing state.
   * - A scheduled downgrade is critical account state, not ephemeral UI state.
   * - The only user action exposed here is explicit cancellation of the
   *   scheduled downgrade itself.
   * ──────────────────────────────────────────────────────────────── */
  const handleCancelScheduledDowngrade = useCallback(async () => {
    if (!cancelScheduledDowngrade) {
      console.error(
          "[ScheduledChangeBanner] Missing cancelScheduledDowngrade " +
          "function in BillingContext.",
      );
      return;
    }

    const customerId = currentSubscription?.customerId || null;

    if (!customerId) {
      console.error(
          "[ScheduledChangeBanner] Cannot cancel scheduled downgrade " +
          "without customerId.",
      );
      return;
    }

    try {
      setIsCancelingScheduledDowngrade(true);

      const result = await cancelScheduledDowngrade({customerId});

      console.log(
          "[ScheduledChangeBanner] cancelScheduledDowngrade result:",
          result,
      );

      if (syncStripeSubscriptionDetails) {
        await syncStripeSubscriptionDetails(customerId);
      }
    } catch (err) {
      console.error(
          "[ScheduledChangeBanner] Failed to cancel scheduled downgrade:",
          err,
      );
    } finally {
      setIsCancelingScheduledDowngrade(false);
    }
  }, [
    cancelScheduledDowngrade,
    currentSubscription?.customerId,
    syncStripeSubscriptionDetails,
  ]);

  /* Debug (keep while validating) */
  // eslint-disable-next-line no-console
  // console.log("[ScheduledChangeBanner] debug:", {
  //   pathname: location.pathname,
  //   hasSub: Boolean(currentSubscription),
  //   pendingStripeSchedule: currentSubscription?.pendingStripeSchedule || null,
  //   isCancelingScheduledDowngrade,
  // });

  if (!isOnSubscriptionPage) return null;
  if (!hasScheduledChange) return null;

  /* ───────────────────── styling (match TrialBanner) ─────────────── */
  const severity = "info";

  const borderColor = theme.palette.info.main;

  const bgColor =
    severity === "info"
      ? theme.palette.background.paper
      : theme.palette.mode === "light"
        ? alpha(theme.palette[severity].main, 0.08)
        : alpha(theme.palette[severity].dark, 0.25);

  /* ───────────────────── render ──────────────────────────────────── */
  return (
    <Alert
      severity={severity}
      icon={false}
      sx={{
        borderRadius: 0,
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar + 1,
        py: 0.75,
        px: 2,
        backgroundColor: bgColor,
        borderLeft: `4px solid ${borderColor}`,
        "& .MuiAlert-message": {width: "100%"},
      }}
    >
      <Stack direction="row" alignItems="center" width="100%">
        <Stack
          direction={isMobile ? "column" : "row"}
          alignItems="center"
          spacing={1}
          sx={{mx: "auto"}}
        >
          <Typography
            variant="body2"
            fontWeight={500}
            textAlign={isMobile ? "center" : "left"}
          >
            {bannerText.map((part, index) => {
              if (
                part &&
                typeof part === "object" &&
                Object.prototype.hasOwnProperty.call(part, "strong")
              ) {
                return (
                  <Box
                    key={`banner-part-${index}`}
                    component="span"
                    sx={{fontWeight: 700}}
                  >
                    {part.strong}
                  </Box>
                );
              }

              return (
                <React.Fragment key={`banner-part-${index}`}>
                  {part}
                </React.Fragment>
              );
            })}

            {" "}

            <Link
              component="button"
              type="button"
              underline="always"
              onClick={handleCancelScheduledDowngrade}
              disabled={isCancelingScheduledDowngrade}
              sx={{
                fontWeight: 700,
                verticalAlign: "baseline",
              }}
            >
              {isCancelingScheduledDowngrade ?
                "Canceling scheduled downgrade..." :
                "Cancel scheduled downgrade"}
            </Link>
          </Typography>
        </Stack>

        {isCancelingScheduledDowngrade && (
          <CircularProgress
            size={16}
            thickness={5}
            sx={{ml: 1}}
          />
        )}
      </Stack>
    </Alert>
  );
}